import { useState, useCallback } from "react";
import { PlacedObject, CatalogObject, ProjectData } from "../../types";
import { updatePlacedObject } from "../utils/objectUtils";
import type { MarkerDragEvent } from "react-map-gl/mapbox";

interface UseObjectLogicProps {
  projectData: ProjectData | null;
  setProjectData: (data: ProjectData | null) => void;
  projectId: string | undefined;
  onQuantityUsed?: (inventoryId: string, quantityUsed: number) => void;
}

export const useObjectLogic = ({
  projectData,
  setProjectData,
  projectId,
  onQuantityUsed,
}: UseObjectLogicProps) => {
  const [placedObjects, setPlacedObjects] = useState<PlacedObject[]>([]);
  const [isPlacingObject, setIsPlacingObject] = useState<boolean>(false);
  const [objectToPlace, setObjectToPlace] = useState<CatalogObject | null>(
    null
  );
  const [selectedObject, setSelectedObject] = useState<PlacedObject | null>(
    null
  );

  const updateProjectObjects = () => {
    if (projectData && placedObjects) {
      const updatedProjectData = {
        ...projectData,
        objects: placedObjects,
      };
      setProjectData(updatedProjectData);
    }
  };

  const handleObjectPlacementStart = (item: CatalogObject | null) => {
    setIsPlacingObject(item !== null);
    setObjectToPlace(item);
    setSelectedObject(null);
  };

  const handleObjectClick = (lng: number, lat: number) => {
    if (!objectToPlace) return;

    const itemToPlace = objectToPlace;
    if (!itemToPlace) return;

    const newObject: PlacedObject = {
      id: `${itemToPlace.id}-${Date.now()}`,
      objectId: itemToPlace.id,
      name: itemToPlace.name,
      type: itemToPlace.type,
      coordinates: { longitude: lng, latitude: lat },
      rotation: 0,
      scale: 1,
    };

    setPlacedObjects((prev) => [...prev, newObject]);
    setIsPlacingObject(false);
    setObjectToPlace(null);

    // Track quantity usage if inventory tracking is enabled
    if (itemToPlace.inventory_id && onQuantityUsed) {
      onQuantityUsed(itemToPlace.inventory_id, 1);
    }

    if (projectId !== "new") {
      saveObjectPlacement(newObject);
    }
  };

  const saveObjectPlacement = async (object: PlacedObject) => {
    // Function Disabled, may be useful for line updates later on
    return;
    // if (!projectData || projectData.id === "new") return;
    // try {
    //   await fetch(`${API_URL}/projects/${projectData.id}/objects`, {
    //     method: "PUT",
    //     headers: { "Content-Type": "application/json" },
    //     body: JSON.stringify(object),
    //   });
    // } catch (error) {
    //   console.error("Error saving object placement:", error);
    // }
  };

  const handleObjectSelect = (object: PlacedObject) => {
    setSelectedObject(object);
    setIsPlacingObject(false);
    setObjectToPlace(null);
  };

  const onMarkerDragEnd = useCallback(
    async (event: MarkerDragEvent) => {
      if (!selectedObject) return;

      const { lng, lat } = event.lngLat;
      const updatedObject: PlacedObject = {
        ...selectedObject,
        coordinates: { longitude: lng, latitude: lat },
      };

      setSelectedObject(updatedObject);
      setPlacedObjects((prev) =>
        prev.map((o) => (o.id === updatedObject.id ? updatedObject : o))
      );

      if (projectId !== "new" && projectId) {
        await updatePlacedObject(projectId, updatedObject, projectData);
      }
    },
    [selectedObject, projectId, projectData]
  );

  return {
    updateProjectObjects,
    handleObjectPlacementStart,
    placedObjects,
    setPlacedObjects,
    isPlacingObject,
    setIsPlacingObject,
    objectToPlace,
    setObjectToPlace,
    selectedObject,
    setSelectedObject,
    handleObjectClick,
    saveObjectPlacement,
    handleObjectSelect,
    onMarkerDragEnd,
  };
};
